import { X, TrendingUp } from "lucide-react";

export default function DetailPanel({ hit, onClose, onClickChat, onClickQuizz, onClickGritAndFit, actionLoading }) {
  if (!hit) return null;

  const salary =
    hit.salary_min && hit.salary_max
      ? `${hit.salary_min.toLocaleString("fr-FR")}€ - ${hit.salary_max.toLocaleString("fr-FR")}€`
      : null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/60"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full z-50 bg-gray-900 border-l border-gray-800 w-full max-w-sm md:max-w-[380px] shadow-[-4px_0_32px_rgba(0,0,0,0.4)] flex flex-col">

        {/* Header avec photo */}
        <div className="shrink-0 relative">
          {hit.photo_url ? (
            <div className="overflow-hidden">
              <img
                src={hit.photo_url}
                alt={hit.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent" />
            </div>
          ) : (
            <div className="h-14 bg-gray-800" />
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 bg-gray-900/80 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-400 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
          <div className={`px-5 ${hit.photo_url ? "absolute bottom-0 left-0 right-0 pb-4" : "pt-3 pb-2"}`}>
            <h2 className="text-lg font-bold text-white leading-tight">{hit.title}</h2>
            {salary && (
              <span className="text-sm font-medium text-teal-400">{salary} / mois</span>
            )}
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto job-scrollbar px-5 py-4 flex flex-col gap-5">

          {/* Citation */}
          {hit.quote && (
            <blockquote className="text-sm text-gray-400 italic border-l-2 border-teal-500 pl-3 leading-relaxed">
              "{hit.quote}"
            </blockquote>
          )}

          {/* Description */}
          {hit.description && (
            <div>
              <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Le métier
              </h4>
              <p className="text-sm text-gray-300 leading-relaxed">{hit.description}</p>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            {hit.categories_label?.map((c) => (
              <span key={c} className="text-xs text-teal-300 bg-teal-500/10 px-2.5 py-1 rounded-full">
                {c}
              </span>
            ))}
            {hit.study_levels_label?.map((s) => (
              <span key={s} className="text-xs text-orange-300 bg-orange-500/10 px-2.5 py-1 rounded-full">
                {s}
              </span>
            ))}
          </div>

          {/* Évolution salariale */}
          {hit.salary_evolutions?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Évolution salariale
              </h4>
              <div className="flex flex-col gap-2">
                {hit.salary_evolutions.map((step, i) => (
                  <div
                    key={i}
                    className="bg-gray-800 rounded-xl p-3 flex items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white">
                        {step.experience}
                        <span className="font-normal text-gray-500 ml-1">({step.experience_years})</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                        {step.experience_role}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-teal-400 shrink-0 whitespace-nowrap">
                      {step.experience_salary}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ce métier est fait pour toi */}
          {hit.made_for_you?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Ce métier est fait pour toi si…
              </h4>
              <ul className="flex flex-col gap-1.5">
                {[...new Set(hit.made_for_you)].slice(0, 6).map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-teal-500 mt-0.5 shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Qualités */}
          {hit.qualities_label?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Qualités requises
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {hit.qualities_label.map((q) => (
                  <span key={q} className="text-xs text-blue-300 bg-blue-500/10 px-2.5 py-1 rounded-full">
                    {q}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Intérêts */}
          {hit.interests_label?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Centres d'intérêt
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {hit.interests_label.map((it) => (
                  <span key={it} className="text-xs text-purple-300 bg-purple-500/10 px-2.5 py-1 rounded-full">
                    {it}
                  </span>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* CTA Buttons */}
        {(onClickChat || onClickQuizz || onClickGritAndFit) && (
          <div className="shrink-0 px-5 py-4 border-t border-gray-800 flex flex-col gap-2">
            {onClickChat && (
              <button
                onClick={onClickChat}
                disabled={actionLoading}
                className="w-full flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 text-sm font-semibold transition"
              >
                <span className="flex items-center gap-2">
                  {actionLoading ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-slate-900" /> : <span>💬</span>}
                  Pose tes questions à un pro
                </span>
                <span className="text-xs font-normal opacity-70">Aucune mauvaise question, que de la découverte !</span>
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              {onClickQuizz && (
                <button
                  onClick={onClickQuizz}
                  disabled={actionLoading}
                  className="flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:brightness-110 disabled:opacity-50 text-white text-sm font-semibold transition"
                >
                  <span>🎯 Teste-toi ?</span>
                  <span className="text-[10px] font-normal opacity-80">4 min • 6 questions</span>
                </button>
              )}
              {onClickGritAndFit && (
                <button
                  onClick={onClickGritAndFit}
                  disabled={actionLoading}
                  className="flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl bg-gradient-to-r from-orange-600 to-red-600 hover:brightness-110 disabled:opacity-50 text-white text-sm font-semibold transition"
                >
                  <span>🎮 Vis une journée</span>
                  <span className="text-[10px] font-normal opacity-80">7-10 min • 3 défis</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
